/** `document.documentElement` attribute when the deferred compliance bar is visible. */
export const COMPLIANCE_STICKY_BAR_DATA_ATTR = "data-compliance-sticky-bar";

/** Measured bar height (px); falls back when not yet measured. */
export const COMPLIANCE_STICKY_BAR_HEIGHT_VAR = "--compliance-sticky-bar-height";

/**
 * Warning-lane chrome for the defer bar — aligned with `Alert variant="warning"` and
 * safelist limit banners (opaque amber, not sheet `bg-card`).
 */
export const COMPLIANCE_STICKY_BAR_SHELL_CLASS =
  "border-t-2 border-amber-400 bg-amber-50 shadow-[0_-4px_24px_-4px] shadow-amber-900/12 dark:border-amber-600 dark:bg-amber-950 dark:shadow-black/40";

/** CTA on amber shell — high contrast without duplicating primary Submit styling. */
export const COMPLIANCE_STICKY_BAR_REVIEW_BUTTON_CLASS =
  "border-amber-500/80 bg-background text-amber-900 shadow-sm hover:bg-amber-100/90 hover:text-amber-950 dark:border-amber-500 dark:bg-amber-950 dark:text-amber-100 dark:hover:bg-amber-900";

/** ~4.25rem inner chrome before safe-area (used until ResizeObserver runs). */
const STICKY_BAR_CHROME_PX = 68;

export function setComplianceStickyBarHeight(px: number) {
  document.documentElement.style.setProperty(
    COMPLIANCE_STICKY_BAR_HEIGHT_VAR,
    `${Math.ceil(px)}px`,
  );
}

export function clearComplianceStickyBarHeight() {
  document.documentElement.style.removeProperty(COMPLIANCE_STICKY_BAR_HEIGHT_VAR);
}

/** Probed safe-area bottom from Layout (`--app-safe-area-bottom`), with env() fallback. */
export function readAppSafeAreaBottomPx(): number {
  const root = getComputedStyle(document.documentElement);
  const probed = Number.parseFloat(root.getPropertyValue("--app-safe-area-bottom"));
  if (Number.isFinite(probed) && probed > 0) {
    return probed;
  }
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;visibility:hidden;pointer-events:none;padding-bottom:env(safe-area-inset-bottom,0px)";
  document.documentElement.appendChild(probe);
  const envBottom = Number.parseFloat(getComputedStyle(probe).paddingBottom) || 0;
  probe.remove();
  return envBottom;
}

export function getComplianceStickyBarHeightFallback(): number {
  return STICKY_BAR_CHROME_PX + readAppSafeAreaBottomPx();
}

/**
 * Keeps `--compliance-sticky-bar-height` in sync with the rendered bar (iOS orientation,
 * visualViewport chrome, font load). Call from ComplianceStickyBar on mount.
 */
export function mountComplianceStickyBarHeightSync(node: HTMLElement): () => void {
  const sync = () => {
    setComplianceStickyBarHeight(node.getBoundingClientRect().height);
  };

  setComplianceStickyBarHeight(getComplianceStickyBarHeightFallback());

  requestAnimationFrame(() => {
    sync();
    requestAnimationFrame(sync);
  });

  const observer = new ResizeObserver(sync);
  observer.observe(node);

  const onViewportChange = () => sync();
  window.visualViewport?.addEventListener("resize", onViewportChange);
  window.addEventListener("orientationchange", onViewportChange);
  window.addEventListener("resize", onViewportChange);

  return () => {
    observer.disconnect();
    window.visualViewport?.removeEventListener("resize", onViewportChange);
    window.removeEventListener("orientationchange", onViewportChange);
    window.removeEventListener("resize", onViewportChange);
    clearComplianceStickyBarHeight();
  };
}
